import type { OperationId, UserRole } from '@ctcells/contract'

import type { Permission } from '../paths.ts'

/**
 * 🔴 **권한 5종 ↔ 대표 operationId 표 — 권한 판정의 원천을 `GET /auth/me` 로 옮기는 다리.**
 *
 * ## 왜 있는가
 *
 * 서버가 `GET /auth/me` 로 `{ user_id, role, operations[] }` 를 준다(2026-09-26 실측 · admin 90 ·
 * operator 76 · viewer 23). `operations` 는 그 역할이 부를 수 있는 **정본 operationId** 목록이고
 * 원천은 계약 `x-roles` 다(`ctcells-core` `app/views/auth.py` `get_me` → `app/core/operation_roles.py`).
 * ⇒ 「이 역할이면 이 권한」을 **클라이언트가 손으로 쥐던 표**(`ROLE_PERMISSIONS`)를 서버가 준 목록으로 대신한다.
 *
 * 화면은 여전히 「권한 5종」으로 묻는다(`access.ts` · `gate({ perm })`). 이 표가 그 물음을
 * 「그 권한의 **대표 operationId 가 전부** `operations` 에 있나」로 바꾼다.
 *
 * ## 🔴 판정 규칙 — 「하나라도」가 아니라 «전부»
 *
 * 권한 하나에 대표 operationId 를 여럿 둔다. **전부 있어야** 그 권한이 있다.
 * 「하나라도 있으면」으로 두면 서버가 그 축의 연산 하나만 열어 줘도 축 전체가 열린 것으로 읽힌다
 * — 실제로 viewer 는 `heartbeatControlLock` 을 받지만 `acquireControlLock` 은 못 받는다(실측).
 * 좁게 넘어지는 쪽(비활성 + 사유)이 안전하고, 집행은 어차피 서버 403 이다.
 *
 * ## 대표를 고른 근거 (정본 `orchestrator_api.openapi.yaml` `x-roles` · 줄번호 2026-09-26)
 *
 * | 권한 | 대표 operationId | `x-roles` | 근거 |
 * | --- | --- | --- | --- |
 * | `view` | `listSamples` · `getSample` · `listHistory` · `listLogs` | admin·operator | PII 접촉면 — 정본 `07 §2.4` 「시료·이력·로그 viewer ✗」 · yaml 1028~1050 |
 * | `monitor` | `listModules` · `listBatches` · `listAlarms` · `getSafetyState` | admin·operator·viewer | 모니터링 스냅샷 4종 · yaml 1009·1041~1045 |
 * | `control` | `acquireControlLock` · `createBatch` · `startBatch` · `stopBatch` · `ackAlarm` | admin·operator | 제어락 → 배치 → 알람 확인 · yaml 996·1010~1014·1046 |
 * | `settings` | `listUsers` · `createUser` · `updateUser` · `listSessions` · `revokeSession` | admin | 계정·세션 관리 · yaml 1124~1134 |
 * | `maintenance` | `declareModuleServiceTermination` · `reinstateModuleService` | admin | 모듈 정비 종료 선언·복귀 · yaml 1189~1190 |
 *
 * ⚠ `recordCalibration` · `runModuleDiagnosticsControl` 은 이름은 정비 같지만 **operator 에게도 열린다**
 *   (`x-roles: [admin, operator]`) — `maintenance` 대표로 쓰면 operator 가 정비 권한을 얻는다. 넣지 마라.
 *   (보정 기록이 `control` 축인 이유는 `ModuleDetailScreen` 주석에 있다.)
 * ⚠ 이름의 출처는 **정본 생성물**(`OperationId` = `keyof operations`)이다. 실서버 `openapi.json` 의
 *   `operationId` 는 FastAPI 자동 이름이라 이 표와 대조할 수 없다(2026-09-26 실측). 없는 이름을 적으면
 *   **타입체크가 깨진다.** 실물 목록과의 대조는 `packages/ui/src/test/permissionOperations.test.ts` 가 한다.
 *
 * 🔴 **실물 3역할로 판정하면 종전 `ROLE_PERMISSIONS` 와 같은 집합이 나온다**(시험이 건다).
 *    갈아타는 것은 «원천»이지 «결과»가 아니다 — 결과가 달라지면 이 표가 틀린 것이다.
 */
export const PERMISSION_OPERATIONS: Readonly<Record<Permission, readonly OperationId[]>> = {
  view: ['listSamples', 'getSample', 'listHistory', 'listLogs'],
  monitor: ['listModules', 'listBatches', 'listAlarms', 'getSafetyState'],
  control: ['acquireControlLock', 'createBatch', 'startBatch', 'stopBatch', 'ackAlarm'],
  settings: ['listUsers', 'createUser', 'updateUser', 'listSessions', 'revokeSession'],
  maintenance: ['declareModuleServiceTermination', 'reinstateModuleService'],
}

/** 권한 5종 — 판정 결과의 순서를 고정한다(역할 표와 같은 순서) */
export const PERMISSIONS: readonly Permission[] = [
  'view',
  'monitor',
  'control',
  'settings',
  'maintenance',
]

/** `operations` 로 권한 5종을 판정한다 — 대표 operationId 가 **전부** 있어야 그 권한이다 */
export function permissionsForOperations(operations: readonly string[]): readonly Permission[] {
  const held = new Set(operations)
  return PERMISSIONS.filter((perm) => PERMISSION_OPERATIONS[perm].every((op) => held.has(op)))
}

/**
 * 역할 → 권한 표(종전 원천). 🔴 **`operations` 를 아직 못 받았을 때의 대체**다.
 *
 * - 집행은 **서버 403 `INSUFFICIENT_ROLE`** 이다. 이 표는 버튼을 「비활성 + 사유」로 만들 뿐이다.
 * - ⚠ `service` 는 계약 enum 에 없다(와이어프레임 4번째 역할).
 *   🔴 **서버에 이 역할은 실재하지 않는다**(2026-09-21 실측) — 역할 어휘가
 *   `app_user.role CHECK (role IN ('admin','operator','viewer'))` 에서 파생되고
 *   (`services/auth.py` `_roles_from_canon()`) `require_roles` 가 어휘 밖 역할을
 *   **정의 시점에** 죽인다. 즉 이 가지는 **도달하지 않는다.** 지우지 않는 이유는 모르는 역할이
 *   왔을 때 빈 집합(= 전부 비활성)으로 떨어지는 편이 안전해서다.
 *
 * 🔴 **`view` 는 「PII 접촉면을 볼 수 있는가」다**(2026-09-21 정정) — viewer 에게 없다.
 *    서버(`views/samples.py` `READ_ROLES = {operator, admin}`) · 계약(`listSamples` `x-roles`) ·
 *    정본 `07 §2.4` 셋이 일치한다. 문(사이드바)은 가장 약한 권한으로 두고 **탭이 제 권한을 건다**
 *    (`resourceTabs.ts`) — 그래야 viewer 가 레시피·랩웨어(서버 200)를 잃지 않는다.
 */
export const ROLE_PERMISSIONS: Record<UserRole, readonly Permission[]> = {
  admin: ['view', 'monitor', 'control', 'settings', 'maintenance'],
  operator: ['view', 'monitor', 'control'],
  /** 🔴 `view` 가 **없다** — 서버가 viewer 의 PII 경로를 403 으로 막는다 */
  viewer: ['monitor'],
  /** 🔴 도달 불가 — 서버에 없는 역할이다(위 머리말). `view` 가 없어 시료 ID 가 마스킹된다 */
  service: ['monitor', 'maintenance'],
}

/** 역할을 모르면 빈 집합 — 조작 버튼이 전부 비활성 + 사유가 되는 쪽이 안전한 기본값이다 */
export function permissionsForRole(role: UserRole | undefined): readonly Permission[] {
  return role === undefined ? [] : (ROLE_PERMISSIONS[role] ?? [])
}

/**
 * 🔴 **판정 규칙 한 줄: `operations` 가 있으면 그것이 이기고, 없으면(`null`) 역할 표다.**
 *
 * `null` 은 「아직 못 받았다」(로그인 직후 · `/auth/me` 실패)다 — 그때 화면이 텅 비면 안 되니
 * 종전 표로 떨어진다. 빈 배열 `[]` 은 「받았는데 없다」라서 **역할 표로 떨어지지 않는다.**
 */
export function resolvePermissions(
  role: UserRole | undefined,
  operations: readonly string[] | null,
): readonly Permission[] {
  return operations === null ? permissionsForRole(role) : permissionsForOperations(operations)
}

/**
 * 경로 단위 게이트 — 「이 operationId 를 부를 수 있나」.
 *
 * ⚠ **아직 부르는 화면이 없다**(2026-09-26 — 이번엔 권한 5종 판정만 갈아탔다). 문 하나 · 버튼 하나를
 *   연산 단위로 걸어야 할 때 쓴다.
 * 🔴 `operations` 를 모르면(`null`) **거짓**이다. 역할 표는 권한 5종 단위라 연산 하나의 답을 줄 수 없고,
 *    지어내면 상태 추론이다 — 비활성 + 사유로 넘어진다(집행은 서버 403).
 */
export function canCallOperation(
  operations: readonly string[] | null,
  operationId: OperationId,
): boolean {
  return operations?.includes(operationId) ?? false
}
